import axios from 'axios'
import { getAuthHeaders, handleApiError, getErrorMessage, ApiResponse } from './apiConfig'

// Utiliser l'instance axios globale configurée dans main.ts
const http = axios

interface ListMembersParams {
  pageNumber?: number
  sortDir?: string
  sortColumn?: string
  sortOption?: string
  borneInf?: string
  borneSup?: string
  pays_id?: string
  collectivite_id?: string
  search?: string
  trashed?: number
}

interface Member {
  id: number
  nom: string
  prenom: string
  telephone: string
  email: string
  photo?: string
  collectivite?: {
    id: number
    nom: string
  }
  pays?: {
    id: number
    nom: string
  }
  created_at: string
  actif: number
  deleted_at?: string | null
  fonction?: string
}

interface ListMembersResponse {
  data: Member[]
  total: number
  per_page: number
  last_page: number
}

class MembersService {
  async getStartupInfos() {
    try {
      const response = await http.get('/backoffice/users/get_startupinfos_list_datas', {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async listMembers(params: ListMembersParams = {}): Promise<ListMembersResponse> {
    try {
      const queryString = new URLSearchParams(
        Object.fromEntries(
          Object.entries(params).map(([k, v]) => [k, String(v)])
        )
      ).toString()
      
      // Utiliser la route frontoffice dédiée si collectivite_id est présent, sinon backoffice
      const hasCollectiviteId = params.collectivite_id && params.collectivite_id !== ''
      const endpoint = hasCollectiviteId 
        ? `/collectivites/membres/list?${queryString}`
        : `/backoffice/users/list?${queryString}`
      
      const response = await http.get(endpoint, {
        headers: getAuthHeaders()
      })
      
      // LengthAwarePaginator retourne directement les propriétés
      // La réponse devrait avoir: data, total, per_page, last_page, current_page, etc.
      return response.data
    } catch (error: any) {
      console.error('Erreur listMembers:', error)
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async getMember(id: number): Promise<Member> {
    try {
      const response = await http.get(`/backoffice/users/get/${id}`, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async deleteMember(id: number): Promise<ApiResponse> {
    try {
      const response = await http.post(`/backoffice/users/destroy/${id}`, {}, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async restoreMember(id: number): Promise<ApiResponse> {
    try {
      const response = await http.post(`/backoffice/users/restore/${id}`, {}, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async toggleEtatValide(ids: number[]): Promise<ApiResponse> {
    try {
      const response = await http.post('/backoffice/users/toggle_etat_valide', { ids }, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async exportToExcel(params: ListMembersParams = {}): Promise<Blob> {
    try {
      const queryString = new URLSearchParams(
        Object.fromEntries(
          Object.entries(params).map(([k, v]) => [k, String(v)])
        )
      ).toString()
      
      const response = await http.get(`/backoffice/users/export_excel?${queryString}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async exportToPdf(params: ListMembersParams = {}): Promise<Blob> {
    try {
      const queryString = new URLSearchParams(
        Object.fromEntries(
          Object.entries(params).map(([k, v]) => [k, String(v)])
        )
      ).toString()
      
      const response = await http.get(`/backoffice/users/export_pdf?${queryString}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async updateMember(formData: FormData): Promise<ApiResponse> {
    try {
      const response = await http.post('/backoffice/users/save', formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async sendConfirmationEmail(userIds: number[]): Promise<ApiResponse> {
    try {
      const response = await http.post('/backoffice/users/send_confirmation_email', { users_ids: userIds }, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  // Méthodes spécifiques pour le frontoffice (gérant de collectivité)
  async downloadCsvTemplate(): Promise<Blob> {
    try {
      const response = await http.get('/collectivites/membres/download_csv_template', {
        headers: getAuthHeaders(),
        responseType: 'blob'
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async prepareImportFile(file: File): Promise<any[]> {
    try {
      const formData = new FormData()
      formData.append('import_file', file)
      
      const response = await http.post('/collectivites/membres/prepare_import_file', formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async importFileDatas(importDatas: any[]): Promise<ApiResponse> {
    try {
      const response = await http.post('/collectivites/membres/import_file_datas', {
        import_datas: importDatas
      }, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async countUnverifiedMembers(): Promise<{ count: number }> {
    try {
      const response = await http.get('/collectivites/membres/count_unverified', {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async sendInvitationsUnverified(): Promise<ApiResponse> {
    try {
      const response = await http.post('/collectivites/membres/send_invitations_unverified', {}, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async getMemberFormDatas(id: number): Promise<{ user: Member, collectivite_id: number }> {
    try {
      const response = await http.get(`/collectivites/membres/${id}/form_datas`, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  // Méthode spécifique pour le frontoffice (gérant de collectivité)
  async updateMemberFrontoffice(id: number, formData: FormData): Promise<ApiResponse> {
    try {
      const response = await http.post(`/collectivites/membres/${id}/update`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async getMemberPrelevementsMensuels(id: number): Promise<{
    items: Array<{
      id: number
      souscription_id: number
      mois: number
      date_prevision: string | null
      date_operation: string | null
      montant: number
      statut: 'pending' | 'done' | 'cancelled'
      countdown: string | null
    }>,
    stats: { total_attendu: number, total_effectue: number, percent: number }
  }> {
    try {
      const response = await http.get(`/collectivites/membres/${id}/prelevements_mensuels`, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async getMemberDecaissementsMensuels(id: number): Promise<{
    items: Array<{
      id: number
      mois: number
      date_prevision: string | null
      date_operation: string | null
      montant: number
      percent: number
      statut: 'pending' | 'done' | 'cancelled'
      countdown: string | null
    }>,
    stats: {
      debloque: number
      debloque_percent: number
      bloque: number
      bloque_percent: number
      total: number
    }
  }> {
    try {
      const response = await http.get(`/collectivites/membres/${id}/decaissements_mensuels`, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async getMemberWalletInfos(id: number): Promise<{
    solde: number
    solde_percent: number
    utilise: number
    utilise_percent: number
    dernier_decaissement: number
    dernier_decaissement_percent: number
    dernier_decaissement_date: string | null
  }> {
    try {
      const response = await http.get(`/collectivites/membres/${id}/wallet_infos`, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async getMemberCartes(id: number): Promise<{
    cartes: Array<{
      id: number
      alias: string | null
      numero_masque: string | null
      bloc4: string | null
      type: string | null
      statut: string
      solde: number
      expiration_mois: number | null
      expiration_annee: number | null
      reference: string | null
      num_lot: string | null
      date_retrait: string | null
      created_at: string | null
      par_defaut: boolean
      banque: { id: number; nom: string; slug: string } | null
    }>
  }> {
    try {
      const response = await http.get(`/collectivites/membres/${id}/cartes`, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async bloquerMemberCarte(id: number, carteId: number): Promise<ApiResponse> {
    try {
      const response = await http.post(`/collectivites/membres/${id}/cartes/${carteId}/bloquer`, {}, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async retraitMemberCarte(id: number, carteId: number, payload: { bloc4: string; code_retrait: string; telephone: string }): Promise<ApiResponse> {
    try {
      const response = await http.post(`/collectivites/membres/${id}/cartes/${carteId}/retrait`, payload, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async getMemberCarteTransactions(
    id: number,
    carteId: number,
    filters: { reference?: string; date_min?: string; date_max?: string; page?: number; per_page?: number } = {}
  ): Promise<{
    data: Array<{
      id: number
      reference: string | null
      libelle: string | null
      description: string | null
      montant: number
      devise: string | null
      type: string
      statut: string
      categorie_slug: string | null
      created_at: string | null
    }>
    total: number
    page: number
    per_page: number
  }> {
    try {
      const response = await http.get(`/collectivites/membres/${id}/cartes/${carteId}/transactions`, {
        headers: getAuthHeaders(),
        params: filters,
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }

  async validateMemberPrelevementMensuel(id: number, detailId: number): Promise<ApiResponse> {
    try {
      const response = await http.post(`/collectivites/membres/${id}/prelevements_mensuels/${detailId}/validate`, {}, {
        headers: getAuthHeaders()
      })
      return response.data
    } catch (error: any) {
      const errorMessage = error.message || getErrorMessage(error)
      throw new Error(errorMessage)
    }
  }
}

export default new MembersService()

